import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popover, Whisper, WhisperInstance } from "rsuite";
import { User } from "@shared/models";
import { InitialsAvatar } from "@shared/components/InitialsAvatar";
import { useLogoutImmediate, useOutsideClick } from "@shared/hooks";
import { ReactComponent as LogoutIcon } from "@assets/images/icons/navbar/logout.svg";

import { Tooltip } from "../../../../Tooltip";

import "./index.scss";

interface AvatarMenuProps {
  /** The signed-in user, or `null` while signed out/still loading -- the trigger still renders (an
   *  empty initials circle, generic accessible name) either way, since Logout never depended on the
   *  user having loaded. */
  user: User | null;
}

/**
 * The sidebar's bottom avatar: a round trigger showing the user's photo (falling back to initials)
 * that opens a single-item "Logout" menu to its right, bottom-aligned against the rail. Choosing
 * Logout here has no confirm step -- opening the menu is already the deliberate first step.
 *
 * @param props.user - the signed-in user, or `null`.
 */
const AvatarMenu: FC<AvatarMenuProps> = ({ user }) => {
  const logout = useLogoutImmediate();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const whisperRef = useRef<WhisperInstance>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const logoutItemRef = useRef<HTMLButtonElement>(null);

  // Whisper clones the trigger and overwrites any ref placed on it directly, so the trigger is read
  // through Whisper's own imperative handle (`.root`) instead of a ref of our own.
  const triggerRefLike = useMemo<RefObject<HTMLElement>>(
    () => ({
      get current() {
        return whisperRef.current?.root ?? null;
      },
    }),
    [],
  );

  const closeAndRefocusTrigger = useCallback(() => {
    setMenuOpen(false);
    whisperRef.current?.root?.focus();
  }, []);

  // Cast: `RefObject<T>`'s generic constraint requires `contains(target: EventTarget | null)`,
  // stricter than a real DOM node's own `contains(other: Node | null)`.
  const { isOutside } = useOutsideClick(
    triggerRefLike as RefObject<{ contains: (target: EventTarget | null) => boolean }>,
    // Second ref covers the popover panel: rsuite portals it to `document.body`, so it's never a DOM
    // descendant of the trigger and a single ref can't cover both.
    popoverRef as RefObject<{ contains: (target: EventTarget | null) => boolean } | null>,
  );

  useEffect(() => {
    if (isOutside && isMenuOpen) {
      closeAndRefocusTrigger();
    }
  }, [isOutside, isMenuOpen, closeAndRefocusTrigger]);

  useEffect(() => {
    if (isMenuOpen) {
      logoutItemRef.current?.focus();
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    // Escape is handled by hand, not rsuite's own `rootClose`: its keyup listener never actually
    // closed this popover under this app's jsdom test harness (measured).
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAndRefocusTrigger();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen, closeAndRefocusTrigger]);

  const fullName = user ? `${user.first_name} ${user.last_name}`.trim() : "";
  const tooltipText = fullName || "Account";
  const accessibleLabel = fullName ? `Account menu for ${fullName}` : "Account menu";

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
  };

  return (
    // `forceClosed`: while the mouse never leaves the trigger between hover and click, no new hover
    // event ever fires to close it on its own -- it would otherwise sit on top of the open menu (a
    // higher z-index, node_modules/rsuite/dist/rsuite.min.css: tooltip 1070 vs popover 1060) and
    // intercept the Logout click. `block`: same reason as every other sidebar row (SidebarItem.tsx) --
    // without it the anchor sizes to the button's own content instead of the full rail.
    <Tooltip text={tooltipText} placement="right" block forceClosed={isMenuOpen}>
      <Whisper
        ref={whisperRef}
        trigger="click"
        placement="rightEnd"
        rootClose={false}
        open={isMenuOpen}
        speaker={
          <Popover className="avatar-menu-popover" arrow={false}>
            {/* Ref goes on this inner div, not <Popover> itself -- Overlay clones the popover and
                would overwrite a ref placed directly on it, same as Whisper does to the trigger. */}
            <div className="avatar-menu" role="menu" aria-label="Account" ref={popoverRef}>
              <button
                ref={logoutItemRef}
                type="button"
                role="menuitem"
                className="avatar-menu-item"
                onClick={handleLogout}
              >
                <LogoutIcon className="avatar-menu-item-icon" aria-hidden="true" focusable="false" />
                Logout
              </button>
            </div>
          </Popover>
        }
      >
        <button
          type="button"
          className="avatar-menu-trigger"
          aria-label={accessibleLabel}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <InitialsAvatar
            firstName={user?.first_name ?? ""}
            lastName={user?.last_name ?? ""}
            size={40}
            imageUrl={user?.avatar_url}
          />
        </button>
      </Whisper>
    </Tooltip>
  );
};

export default AvatarMenu;
